import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import InputSelect from '@/components/atoms/InputSelect';
import { useGetReportPeriodsLKQuery } from '@/store/services/reports';
import {
  setReportPeriod,
  updateReportDateFilters,
  useReportPeriod,
} from '@/store/slices/reportFiltersSlice';
import { Calendar } from 'iconsax-react';
import dayjs from 'dayjs';
import { PeriodKey, periodMapper } from './DatesFilters';

export interface PeriodSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  placeholder?: string;
  name?: string;
  id?: string;
  width?: string;
}

export default function PeriodSelect({
  value: controlledValue,
  onChange: controlledOnChange,
  className,
  placeholder,
  name = 'period',
  id = 'period',
  width = 'w-48',
}: PeriodSelectProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportPeriod = useReportPeriod();
  const { data, isLoading } = useGetReportPeriodsLKQuery();

  const periodList = data || [];

  const currentPeriod =
    controlledValue !== undefined ? controlledValue : reportPeriod;

  const getCode = (id: string) => periodList?.find((i) => i.id === id)?.code;

  // Reset period to default (3 months) when navigating to page / mounting component
  useEffect(() => {
    if (periodList && periodList.length > 0) {
      const defaultItem =
        periodList.find((i) => i.code === 'THREE_MONTHS') || periodList[0];
      if (defaultItem) {
        dispatch(setReportPeriod(defaultItem.id));
        const days = periodMapper[defaultItem.code as PeriodKey] || 90;
        dispatch(
          updateReportDateFilters({
            fromDate: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
            toDate: dayjs().format('YYYY-MM-DD'),
          })
        );
      }
    }
  }, [dispatch, periodList.length]);

  const handlePeriodChange = (val: unknown) => {
    const stringVal = String(val ?? '');
    if (controlledOnChange) {
      controlledOnChange(stringVal);
    }
    const code = getCode(stringVal);
    const days = periodMapper[code as PeriodKey] || 90;
    dispatch(setReportPeriod(stringVal));
    dispatch(
      updateReportDateFilters({
        fromDate: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
        toDate: dayjs().format('YYYY-MM-DD'),
      })
    );
  };

  const options = periodList.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  return (
    <div className={width} data-testid="period-select-container">
      <InputSelect
        name={name}
        id={id}
        placeholder={placeholder || t('reports.selectPeriod')}
        options={options}
        loading={isLoading}
        value={currentPeriod || undefined}
        onChange={handlePeriodChange}
        className={className}
        prefix={<Calendar size={18} className="mx-2" />}
      />
    </div>
  );
}
